import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { TEXT_AREA_VARIANTS, TextareaProps, cn } from '@dashboard/ui'
import { useKey } from 'react-use'

export interface AutocompleteTextareaProps extends TextareaProps {
  placeholder?: string
  className?: string
  renderOverlay?: (ctx: { close: () => void }) => React.ReactNode
  keepOpenOnBlur?: boolean
  onCarePositionChange?: (position: number) => void
}

export default function AutocompleteTextarea({
  value,
  onChange,
  placeholder,
  color = 'primary',
  size = 'md',
  renderOverlay,
  keepOpenOnBlur = false,
  onCarePositionChange,
  ...rest
}: AutocompleteTextareaProps) {
  const taRef = useRef<HTMLTextAreaElement | null>(null)
  const mirrorRef = useRef<HTMLDivElement | null>(null)
  const overlayRef = useRef<HTMLDivElement | null>(null)

  const [open, setOpen] = useState(false)
  const [coords, setCoords] = useState<{ left: number; top: number; lineHeight: number }>({
    left: 0,
    top: 0,
    lineHeight: 18,
  })

  const close = useCallback(() => {
    setOpen(false)
    taRef.current?.focus()
  }, [])

  useEffect(() => {
    const mirror = document.createElement('div')
    mirror.style.position = 'absolute'
    mirror.style.visibility = 'hidden'
    mirror.style.whiteSpace = 'pre-wrap'
    mirror.style.wordWrap = 'break-word'
    mirror.style.top = '0'
    mirror.style.left = '-99999px'
    mirror.style.pointerEvents = 'none'
    document.body.appendChild(mirror)
    mirrorRef.current = mirror
    return () => {
      document.body.removeChild(mirror)
      mirrorRef.current = null
    }
  }, [])

  useKey('Escape', close, undefined, [close])

  const computeCaretPosition = useCallback(() => {
    const ta = taRef.current
    const mirror = mirrorRef.current
    if (!ta || !mirror) {
      return
    }

    const selEnd = ta.selectionEnd ?? ta.value.length

    // Copy layout-affecting styles from the textarea to the mirror
    const cs = window.getComputedStyle(ta)
    const propsToCopy: (keyof CSSStyleDeclaration)[] = [
      'boxSizing',
      'width',
      'height',
      'overflowX',
      'overflowY',
      'borderTopWidth',
      'borderRightWidth',
      'borderBottomWidth',
      'borderLeftWidth',
      'paddingTop',
      'paddingRight',
      'paddingBottom',
      'paddingLeft',
      'fontStyle',
      'fontVariant',
      'fontWeight',
      'fontStretch',
      'fontSize',
      'fontFamily',
      'lineHeight',
      'letterSpacing',
      'textTransform',
      'textAlign',
      'tabSize',
      'whiteSpace',
    ]
    propsToCopy.forEach(prop => {
      // @ts-expect-error - CSSStyleDeclaration index
      mirror.style[prop] = cs[prop] as string
    })

    mirror.style.whiteSpace = 'pre-wrap' // ensure wrapping like textarea
    mirror.style.width = cs.width // match width for wrapping

    // Mirror content up to caret and place a marker
    const before = ta.value.substring(0, selEnd)
    mirror.textContent = ''
    const beforeNode = document.createTextNode(before)
    const caretMarker = document.createElement('span')
    caretMarker.textContent = '\u200b' // zero-width space so span has dimensions
    mirror.appendChild(beforeNode)
    mirror.appendChild(caretMarker)

    mirror.scrollTop = ta.scrollTop

    const lineHeight = parseFloat(cs.lineHeight || '18') || 18

    const caretLeft = caretMarker.offsetLeft
    const caretTop = caretMarker.offsetTop

    const taRect = ta.getBoundingClientRect()
    const left = taRect.left + caretLeft - ta.scrollLeft + window.scrollX
    const top = taRect.top + caretTop - ta.scrollTop + window.scrollY

    setCoords({ left, top, lineHeight })
  }, [])

  useLayoutEffect(() => {
    if (!open) {
      return
    }
    computeCaretPosition()
  }, [value, open, computeCaretPosition])

  const onKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (taRef.current) {
        onCarePositionChange?.(taRef.current.selectionStart)
      }
      if (e.ctrlKey && e.altKey && e.code === 'Space') {
        e.preventDefault()
        setOpen(true)
        setTimeout(() => {
          const menuItem = overlayRef?.current?.querySelector('li[tabindex]') as HTMLLIElement
          if (menuItem) {
            menuItem.focus()
          }
        }, 100)

        requestAnimationFrame(() => computeCaretPosition())
        return
      }

      if (e.key === 'Escape' && open) {
        e.preventDefault()
        e.stopPropagation()
        setOpen(false)
        return
      }
    },
    [open, computeCaretPosition],
  )

  const onBlur = useCallback(() => {
    if (!keepOpenOnBlur) {
      setTimeout(() => {
        const overlay = overlayRef?.current
        if (overlay && overlay.contains(document.activeElement)) {
          return
        }
        setOpen(false)
      }, 100)
    }
  }, [keepOpenOnBlur])

  return (
    <>
      <textarea
        ref={taRef}
        value={value}
        placeholder={placeholder}
        onChange={onChange}
        onKeyDown={onKeyDown}
        onBlur={onBlur}
        className={cn(TEXT_AREA_VARIANTS({ color, size }), rest.className)}
      />

      {open &&
        createPortal(
          <div
            ref={overlayRef}
            tabIndex={-1}
            id="autocomplete-overlay"
            className="absolute"
            style={{
              left: coords.left,
              top: coords.top + coords.lineHeight + 4, // small offset below caret
              zIndex: 9999,
            }}
          >
            {renderOverlay?.({ close })}
          </div>,
          document.body,
        )}
    </>
  )
}
